import { useEffect, useRef, useState } from 'react';
import { businessSecondsBetween } from '../../shared/businessTime';
import { PauseCircle, Play } from 'lucide-react';
import { apiRequest, registerClientWaitHandler, type ClientWait } from './api';
import { AdminDialog } from './AdminUserDialogs';
import { adminButton, adminPrimaryButton } from './userAdminHelpers';

export const elapsedLabel = (seconds: number | null) => {
  if (seconds === null) return 'Sin datos';
  const minutes = Math.floor(Math.max(0, seconds) / 60), hours = Math.floor(minutes / 60);
  return hours >= 24 ? `${Math.floor(hours / 24)} d ${hours % 24} h ${minutes % 60} min` : hours ? `${hours} h ${minutes % 60} min` : `${minutes} min`;
};

export default function ClientWaitPrompt() {
  const [waits, setWaits] = useState<ClientWait[]>([]), [note, setNote] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const pending = useRef<((resume: boolean) => void) | null>(null);
  useEffect(() => {
    registerClientWaitHandler(items => {
      if (pending.current) return Promise.resolve(false);
      setWaits(items); setNote(''); setError('');
      return new Promise(resolve => { pending.current = resolve; });
    });
    return () => { registerClientWaitHandler(null); pending.current?.(false); pending.current = null; };
  }, []);
  const close = (resume: boolean) => { pending.current?.(resume); pending.current = null; setWaits([]); };
  const resume = async () => {
    setBusy(true); setError('');
    try {
      for (const wait of waits) {
        // Re-check: another operator may have resumed it while this reminder was open.
        const current = await apiRequest<{ waiting: ClientWait | null }>(`/api/operations/work-orders/${wait.id}/client-wait`);
        if (current.waiting) await apiRequest(`/api/operations/work-orders/${wait.id}/client-wait/resume`, { method: 'POST', body: JSON.stringify({ note }) });
      }
      window.dispatchEvent(new Event('client-wait-changed'));
      close(true);
    } catch (caught) { setError(caught instanceof Error ? caught.message : 'No se pudo reanudar.'); }
    finally { setBusy(false); }
  };
  if (!waits.length) return null;
  return <AdminDialog title="¿Seguimos esperando al cliente?" description="Antes de aplicar este cambio, confirma si el cliente ya respondió. El formulario que estabas preparando se conserva." busy={busy} onClose={() => close(false)} footer={<><button className={adminButton} disabled={busy} onClick={() => close(false)}>Seguir esperando · volver al formulario</button><button className={adminPrimaryButton} disabled={busy || note.trim().length < 5} onClick={() => void resume()}><Play size={16} />Ya respondió · reanudar y continuar</button></>}>
    <div className="space-y-3">{waits.map(wait => <div key={wait.id} className="rounded-2xl border border-amber-200 bg-amber-50 p-4"><h3 className="font-bold text-amber-950"><PauseCircle className="mr-2 inline" size={18} />{wait.number}</h3><p className="mt-2 text-sm">{wait.reason}</p><p className="mt-2 text-xs font-bold text-amber-800">En espera (L–V): {elapsedLabel(Math.max(wait.seconds, businessSecondsBetween(wait.since, Date.now())))}</p></div>)}</div>
    <label className="mt-4 block text-sm font-bold">¿Qué entregó o respondió?<textarea value={note} maxLength={2000} onChange={e => setNote(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-200 p-3 font-normal" placeholder="Ej.: Envió el logo corregido y aprobó el arte." /></label>
    {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}
  </AdminDialog>;
}
